import { useEffect, useRef, useState } from 'react';

/**
 * Animate a number from its previous value (or 0 on first load) to `target`.
 * Respects prefers-reduced-motion.
 */
export function useCountUp(target, { duration = 1800, ready = true } = {}) {
    const numeric = Math.max(0, Number(target) || 0);
    const [value, setValue] = useState(0);
    const prevRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        if (!ready) return undefined;

        const reduced =
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (reduced) {
            setValue(numeric);
            prevRef.current = numeric;
            return undefined;
        }

        const from = prevRef.current == null ? 0 : prevRef.current;
        if (from === numeric) {
            setValue(numeric);
            return undefined;
        }

        const start = performance.now();
        const delta = numeric - from;

        const tick = (now) => {
            const t = Math.min(1, (now - start) / duration);
            // Stronger ease-out: fast start, clearer slowdown near the end
            const eased = 1 - (1 - t) ** 4;
            setValue(Math.round(from + delta * eased));
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                prevRef.current = numeric;
            }
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            prevRef.current = numeric;
        };
    }, [numeric, duration, ready]);

    return value;
}
